import { QuickPassStreakState } from '../_types/quickpass-profile'

const LEGACY_STREAK_STORAGE_KEY_PREFIX = 'quickpass:streak:'

/**
 * Builds a YYYY-MM-DD string from a millis timestamp using the local
 * timezone — UTC dates would cause the streak to flip in the middle of
 * the renter's day.
 *
 * @param {number} timestamp - the millis to format
 * @returns {string} the local-timezone YYYY-MM-DD string
 */
const buildLocalDateString = (timestamp: number): string => {
  const date = new Date(timestamp)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Computes a YYYY-MM-DD string for the day `daysAgo` before the given
 * date string. Used to detect whether two visits are calendar-consecutive.
 *
 * @param {string} isoDateString - the YYYY-MM-DD reference date
 * @param {number} daysAgo - how many days to subtract
 * @returns {string} the resulting YYYY-MM-DD string
 */
const buildPriorDateString = (
  isoDateString: string,
  daysAgo: number,
): string => {
  const [year, month, day] = isoDateString.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() - daysAgo)
  return buildLocalDateString(date.getTime())
}

/**
 * Pure streak transition function. Given a previous streak state and the
 * current local-day string, returns the resolved next state. Same-day
 * visits are no-ops. Consecutive days advance. Any longer gap resets.
 *
 * @param {QuickPassStreakState} previous - the renter's current streak state
 * @param {string} todayString - today's YYYY-MM-DD in local time
 * @returns {QuickPassStreakState} the post-visit streak state
 */
const computeNextStreakState = (
  previous: QuickPassStreakState,
  todayString: string,
): QuickPassStreakState => {
  if (previous.lastVisitDateString === todayString) {
    return previous
  }

  if (previous.lastVisitDateString === '') {
    return {
      currentStreakDays: 1,
      longestStreakDays: 1,
      lastVisitDateString: todayString,
    }
  }

  const yesterdayString = buildPriorDateString(todayString, 1)
  const isConsecutive = previous.lastVisitDateString === yesterdayString
  const nextStreak = isConsecutive === true ? previous.currentStreakDays + 1 : 1
  const longest = Math.max(previous.longestStreakDays, nextStreak)

  return {
    currentStreakDays: nextStreak,
    longestStreakDays: longest,
    lastVisitDateString: todayString,
  }
}

/**
 * Reads any legacy device-local streak state stored under the pre-sync
 * localStorage key. Returns null when nothing is there. Used once on
 * first profile load to migrate the existing renter's streak forward.
 *
 * @param {string} quickPassUserId - the user id the legacy key was scoped to
 * @returns {QuickPassStreakState | null} the legacy state, or null when absent
 */
const readLegacyStreakState = (
  quickPassUserId: string,
): QuickPassStreakState | null => {
  try {
    const raw = window.localStorage.getItem(
      `${LEGACY_STREAK_STORAGE_KEY_PREFIX}${quickPassUserId}`,
    )
    if (raw === null) {
      return null
    }
    return JSON.parse(raw) as QuickPassStreakState
  } catch (error) {
    console.warn('[streak] failed to read legacy state', error)
    return null
  }
}

/**
 * Removes the legacy device-local streak entry once it has been folded
 * into the profile.
 *
 * @param {string} quickPassUserId - the user id the legacy key was scoped to
 * @returns {void}
 */
const clearLegacyStreakState = (quickPassUserId: string): void => {
  try {
    window.localStorage.removeItem(
      `${LEGACY_STREAK_STORAGE_KEY_PREFIX}${quickPassUserId}`,
    )
  } catch (error) {
    console.warn('[streak] failed to clear legacy state', error)
  }
}

export {
  buildLocalDateString,
  computeNextStreakState,
  readLegacyStreakState,
  clearLegacyStreakState,
}
